<template>
    <base-page>
        <CustomTable
            class=""
            :module-name="moduleName"
            :module-name-singular="moduleNameSingular"
            :response="response"
            :isLoading="isLoading"
            :tableHeaders="tableHeaders"
            :filters="logFilters"
            :bulkOperations="logBulkOperations"
            :menuOptions="menuOptions"
            default-filter-display="all"
            :defaultOrdering="defaultOrdering"
            :shouldReload="shouldReload"
            :shouldClearSelected="shouldClearSelected"
            :bulkOperationResponse="bulkOperationResponse"
            :reorder-bulk-operations="reorderBulkOperations"
            :reorder-filters="reorderFilters"
            createModalSize="w-3/4 h-5/6"
            @refetch-results="loadAssimilationLogs"
            @clear-filter="clearFilter"
            @change-filters="setFilters"
            @set-filter-options="setFilterOptions"
            @toggle-pinned-filter="togglePinnedFilter"
        >
            <!-- <template #create-form="{ events: { closeCreateForm, refetchResults }, fetchFilter }">
                <CreateCompany
                    :client-options="clientOptions"
                    @refetch-results="refetchResults(fetchFilter)"
                    @close="closeCreateForm"
                />
            </template> -->
            <template #table-row="{ events: { addSelectedResult, removeSelectedResult, refetchResults, openRowDetails }, selectAll, expandAll, fetchFilter, userPermissions, results, clearedSelectedResults }">
                <AssimilationLogRow
                    v-for="assimilationLog in results"
                    :key="assimilationLog.id"
                    :selectAll="selectAll"
                    :expandAll="expandAll"
                    :userPermissions="userPermissions"
                    :clearedSelectedResults="clearedSelectedResults"
                    :assimilation-log="assimilationLog"
                    @refetch-results="refetchResults(fetchFilter)"
                    @select-row="addSelectedResult"
                    @deselect-row="removeSelectedResult"
                    @open-row-details="openRowDetails"
                    @perform-row-action="performRowAction"
                />
                <tr class="bg-gray-200 h-60">
                    <td :colspan="columnSpan" :class="{ 'border-t border-gray-300' : results && results.length > 0}"></td>
                </tr>
            </template>
            <!-- <template #details-page="{ events: { closeDetails, refetchResults }, resultRow, userPermissions, fetchFilter }">
                <CompanyDetails
                    :company="resultRow"
                    :user-permissions="userPermissions"
                    :client-options="clientOptions"
                    @close-details="closeDetails"
                    @refetch-results="refetchResults(fetchFilter)"
                />
            </template> -->
            <template #table-footer="{ events: { getNext, goToFirst, getPrev, goToLast, jumpToPage, setPageSizeFilter }, viewingFromNbr, viewingToNbr, count, selectedResults, totalPages, page }">
                <CompanyFooter
                    :is-loading="isLoading"
                    :viewingFromNbr="viewingFromNbr"
                    :viewingToNbr="viewingToNbr"
                    :count="count"
                    :selectedResults="selectedResults"
                    :numPages="totalPages"
                    :page="page"
                    :column-span="columnSpan"
                    @get-next="getNext"
                    @go-to-first="goToFirst"
                    @get-prev="getPrev"
                    @go-to-last="goToLast"
                    @jump-to-page="jumpToPage"
                    @set-page-size-filter="setPageSizeFilter"
                />
            </template>
        </CustomTable>
    </base-page>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import CustomTable from '../base-components/CustomTable.vue';
import AssimilationLogRow from './AssimilationLogRow.vue';
import CompanyFooter from '../company/CompanyFooter.vue';
import CompanyDetails from '../company/CompanyDetails.vue';
import CreateCompany from '@/components/CreateCompany.vue';
import EditCompany from '../EditCompany.vue'
import CRMDetailsPage from '../CRMDetailsPage.vue'
import InfoModal from '../InfoModal.vue';
import CompanyContactFormVertical from '../CompanyContactFormVertical.vue';
import AllContactsList from '../company-cell-components/AllContactsList.vue';
import { getClients } from '@/services/Client';
import { getSources } from '@/services/Source';
import { getGeoLocations } from '@/services/GeoLocation';
import { getUsersWithTasks, getUsers } from '@/services/User';
import { getActiveCampaigns } from '@/services/Campaign';
import { getCrmStatuses } from '@/services/CrmStatus';
import { getTiers } from '@/services/Tiers';
import { getCollectionTags } from '@/services/CollectionTag';
import { getNAICSCodes } from '@/services/NAICSCode';
import { getReferralSources } from '@/services/Referrer';
import { getAsynchronousJobQueue } from "@/services/AsynchronousJobs";
import { getActions } from '@/services/CrmAction'
import { getEmailTemplates } from '@/services/EmailTemplates';
import { getAllAssimilationLogs } from '@/services/AssimilationLog';
import { formatDateTime } from '@/composables/formatDateTime';
import { getTodaysDateAsString } from '@/composables/formatDate';

const store = useStore()
const $toast = useToast()

const isLoading = ref(false)
const moduleName = ref('Assimilation Logs')
const moduleNameSingular = ref('Assimilation Log');
const response = ref(null)
const defaultOrdering = ref([])
const shouldReload = ref(false)
const shouldClearSelected = ref(false)
const bulkOperationResponse = ref()
const reorderBulkOperations = ref(false)
const reorderFilters = ref(false)
const displayedLog = ref(null)
const visibleEditForm = ref(false)
const actionButtonId = ref(null)
const tableHeaders = ref([
    {
        value: 'surviving_company',
        label: 'Surviving Company',
        class: 'p-0 w-[40%] bg-gray-150 border-b-2'
    },
    {
        value: 'assimilated_company',
        label: 'Assimilated Company',
        class: 'p-0 w-[40%] bg-gray-150 border-b-2'
    },
    {
        value: 'Assimilated On',
        label: 'Assimilated On',
        class: 'p-0 w-[15%] bg-gray-150 border-b-2',
    }
])

const menuOptions = reactive({
    controlPanel: {
        width: 'w-[330px]',
        panes: ['filters']
    },
    quickSearch: false,
    selectAll: false,
    selectAllHeader: false,
    expandAllHeader: false,
    paginationArrows: false,
    totalResults: false,
    pageSize: false,
    refreshButton: false,
    createForm: false,
    addFormRows: false,
})

const logFilters = reactive({
    survivingCompanyId: {
        id: 39,
        name: "surviving_company",
        label: "Surviving Company ID",
        placeholder: '123456',
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        awaitingOptions: false,
        visible: true,
        pinned: true,
    },
    assimilatedCompanyId: {
        id: 40,
        name: "assimilated_company",
        label: "Assimilated Company ID",
        placeholder: '123456',
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        awaitingOptions: false,
        visible: true,
        pinned: true,
    },
    assimilatedOn: {
        id: 41,
        name: "assimilated_on",
        label: "Assimilated On",
        placeholder: '',
        value: null,
        defaultValue: null,
        type: 'date',
        widget: 'input',
        multiSelect: false,
        awaitingOptions: false,
        visible: true,
        pinned: true,
    },
})

const logBulkOperations = reactive({})

const logRowActions = reactive({})

const logSummaryActions = reactive({})

const columnSpan = computed(() => {
    let numHeaders = tableHeaders.value.length
    return menuOptions.selectAllHeader ? numHeaders + 1 : numHeaders
})

function handleActionResponse(response, buttonId, errorMessage) {
    console.log(response)
    if(response.status == 200) {
        $toast.open({
            type: 'success',
            duration: 5000,
            message: response.data.status ? response.data.status : 'Successfully completed action.',
        })
        reloadAssimilationLogs()
    } else {
        $toast.open({
            type: 'error',
            duration: 5000,
            message: errorMessage
        })
    }
    actionButtonId.value = buttonId
}

// function handleBulkActionResponse(actionData, response) {
//     // console.log('handleResponse', response)
//     if(response.status == 200) {
//         $toast.open({
//             message:`Completed bulk operation: ${actionData.label}`,
//             type: 'success',
//             duration: 3000
//         })
//         if(!actionData.keepSelected) {
//             clearSelectedResults()
//         }
//         reloadAssimilationLogs()
//         // fetchAllOptions()
//     } else {
//         $toast.open({
//             message: `Could not complete bulk operation: ${actionData.label}`,
//             type: 'error',
//             duration: 10000
//         })
//     }
//     bulkOperationResponse.value = response
// }

// function clearSelectedResults() {
//     console.log('bulkAction - clearSelectedResults')
//     shouldClearSelected.value = !shouldClearSelected.value
// }

function reloadAssimilationLogs() {
    shouldReload.value = !shouldReload.value
}

function setFilters(updatedFilters) {
    logFilters.survivingCompanyId = updatedFilters.survivingCompanyId;
    logFilters.assimilatedCompanyId = updatedFilters.assimilatedCompanyId;
}

function clearFilter(filterKey) {
    if(filterKey.endsWith('.min')) {
        logFilters[filterKey.replace('.min', '')].min.value = null
    } else if(filterKey.endsWith('.max')) {
            logFilters[filterKey.replace('.max', '')].max.value = null
    } else if(Array.isArray(logFilters[filterKey].value)){
        logFilters[filterKey].value = []
    } else {
        logFilters[filterKey].value = null
    }
}


function closeEditForm() {
    visibleEditForm.value = false
    displayedLog.value = null
}


// function setPinnedFilters(pinnedFilters) {
//     pinnedFilters.forEach((pinnedFilter) => {
//         let filterToPin = Object.values(companyFilters).find((filter) => filter.id == pinnedFilter.company_filter_field)
//         if(filterToPin) {
//             filterToPin.pinned = true
//             filterToPin.persisted_id = pinnedFilter.id
//         }
//     })
//     reorderFilters.value = !reorderFilters.value
// }

// function setPinnedBulkOperations(pinnedBulkOperations) {
//     pinnedBulkOperations.forEach((pinnedBulkOperation) => {
//         let bulkOperationToPin = Object.values(companyBulkOperations).find((bulkOperation) => bulkOperation.id == pinnedBulkOperation.company_bulk_operation_field)
//         bulkOperationToPin.pinned = true
//         bulkOperationToPin.persisted_id = pinnedBulkOperation.id
//     })
//     reorderBulkOperations.value = !reorderBulkOperations.value
// }

function performRowAction(actionData) {
    actionButtonId.value = null
    // console.log('CompanyPage: performRowAction', actionData)
    logRowActions[actionData.actionKey].performAction(actionData)
}

async function setFilterOptions(filterName, queryParams='') {
    // console.log('CompanyPage: setFilterOptions', filterName)
    if(!logFilters[filterName].awaitingOptions){
        if(logFilters[filterName].setOptionsMethod){
            logFilters[filterName].awaitingOptions = true
            logFilters[filterName].setOptionsMethod(queryParams)
            setTimeout(() => {
                logFilters[filterName].awaitingOptions = false;
            }, 30000)
        }
    }
}


async function loadAssimilationLogs(fetchFilter='') {
    isLoading.value = true;
    const token = store.state.auth.token
    response.value = await getAllAssimilationLogs(token, fetchFilter);
    isLoading.value = false;
}

loadAssimilationLogs()

// async function loadPinnedFilters() {
//     // console.log('loadPinnedFilters')
//     const token = store.state.auth.token
//     const userId = store.state.auth.user.id
//     const response = await getPinnedUserCompanyFilterFields(token, userId)
//     // console.log(response)
//     setPinnedFilters(response.data)
// }

async function togglePinnedFilter(filterKey) {
    // console.log('togglePinnedFilter', filterKey)
    let pinnedValue = logFilters[filterKey].pinned
    logFilters[filterKey].pinned = !logFilters[filterKey].pinned
    reorderFilters.value = !reorderFilters.value
    // if(pinnedValue) {
    //     await persistUnpinnedFilter(logFilters[filterKey])
    // } else {
    //     await persistPinnedFilter(logFilters[filterKey])
    // }
    // loadPinnedFilters()
}

// async function persistPinnedFilter(filter) {
//     // console.log('persistPinnedFilter', filter)
//     const token = store.state.auth.token
//     const payload = JSON.stringify({
//         company_filter_field_id: filter.id,
//         pinned: true,
//     })
//     const response = await createPinnedUserCompanyFilterField(token, payload)
//     // console.log(response)
// }

// async function persistUnpinnedFilter(filter) {
//     // console.log('persistUnpinnedFilter', filter)
//     const token = store.state.auth.token
//     const response = await deletePinnedUserCompanyFilterField(token, filter.persisted_id)
//     // console.log(response)
// }

// async function loadPinnedBulkOperations() {
//     // console.log('loadPinnedBulkOperations')
//     const token = store.state.auth.token
//     const userId = store.state.auth.user.id
//     const response = await getPinnedUserCompanyBulkOperationFields(token, userId)
//     // console.log(response)
//     setPinnedBulkOperations(response.data)
// }

// async function togglePinnedBulkOperation(bulkOperationKey) {
//     // console.log('togglePinnedBulkOperation', bulkOperationKey)
//     let pinnedValue = companyBulkOperations[bulkOperationKey].pinned
//     companyBulkOperations[bulkOperationKey].pinned = !companyBulkOperations[bulkOperationKey].pinned
//     reorderBulkOperations.value = !reorderBulkOperations.value
//     if(pinnedValue) {
//         await persistUnpinnedBulkOperation(companyBulkOperations[bulkOperationKey])
//     } else {
//         await persistPinnedBulkOperation(companyBulkOperations[bulkOperationKey])
//     }
//     loadPinnedBulkOperations()
// }

// async function persistPinnedBulkOperation(bulk_operation) {
//     // console.log('persistPinnedBulkOperation', bulk_operation)
//     const token = store.state.auth.token
//     const payload = JSON.stringify({
//         company_bulk_operation_field_id: bulk_operation.id,
//         pinned: true,
//     })
//     const response = await createPinnedUserCompanyBulkOperationField(token, payload)
//     // console.log(response)
// }

// async function persistUnpinnedBulkOperation(bulk_operation) {
//     // console.log('persistUnpinnedBulkOperation', bulk_operation)
//     const token = store.state.auth.token
//     const response = await deletePinnedUserCompanyBulkOperationField(token, bulk_operation.persisted_id)
//     // console.log(response)
// }

// async function performCompanyBulkAction(bulkActionData) {
//     // console.log('performCompanyBulkAction')
//     // console.log(bulkActionData)
//     if(bulkActionData.overrideAction) {
//         companyBulkOperations[bulkActionData.key].performBulkAction(bulkActionData)
//     } else {
//         let response
//         const token = store.state.auth.token
//         if(bulkActionData.type == 'onSelected' && bulkActionData.selectedResults.length > 0) {
//             const selectedResultIds = bulkActionData.selectedResults.map(({id}) => id)
//             response = await performBulkAction(token, selectedResultIds, bulkActionData.endpoint, bulkActionData.payload)
//         } else if (bulkActionData.type == 'onQuery' && bulkActionData.queryParams) {
//             response = await performBulkActionOnQuery(token, bulkActionData.payload, bulkActionData.endpoint, bulkActionData.queryParams)
//         }
//         if(response) {
//             handleBulkActionResponse(bulkActionData, response)
//         }
//     }
// }

// function fetchAllOptions() {
//     loadClientOptions()
//     loadSourceOptions()
//     loadCampaignOptions()
//     loadCampaignChangeRequestorOptions()
//     loadCRMStatusOptions()
//     loadCollectionTagOptions()
//     loadEmailTemplates()
//     loadCollectionTagSourcesOptions()
//     loadDrivingTimeFocusOptions()
//     loadNAICSOptions()
//     loadReferralSourceOptions()
//     loadTierOptions()
//     loadTaskUserOptions()
//     loadCRMUserOptions()
//     loadCRMActionTypes()
//     loadPinnedFilters()
//     loadPinnedBulkOperations()
// }
  
// fetchAllOptions()
</script>